import { useEffect } from "react";
import { DateTime } from "luxon";
import { useTheme } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAppDispatch, useAppSelector } from "../store";
import assinaturaApi from "../api/assinatura.api";
import clienteApi from "../api/cliente.api";
import planoApi from "../api/plano.api";
import ButtonLink from "../components/ButtonLink";
import ListItem from "../components/ListItem";
import Section from "../components/Section";
import SectionSkeleton from "../components/SectionSkeleton";
import formatName from "../helpers/formatName";
import type { Plano } from "../types/plano";
import useTitle from "../hooks/useTitle";
import enqueueSnackbar from "../helpers/enqueueSnackbar";
import { DEFAULT_PLANO_ID, PROPOSTA_PLANO_ID_KEY } from "../helpers/constants";

function BeneficiosPlanoAtual({ plano }: { plano: Plano }) {
  return (
    <Section>
      <Typography variant="h6">Benefícios</Typography>
      <List>
        {plano.permitirDependentes && plano.maximoDependentes > 0 && (
          <ListItem
            primary={`Até ${plano.maximoDependentes} dependentes`}
            icon={<CheckCircleIcon color="primary" />}
          />
        )}
        <ListItem
          primary="Descontos exclusivos em consultas"
          icon={<CheckCircleIcon color="primary" />}
        />
        <ListItem
          primary="Descontos exclusivos em exames"
          icon={<CheckCircleIcon color="primary" />}
        />
        {plano.telemedicina && (
          <ListItem
            primary="Telemedicina gratuita para qualquer dia e hora, sem agendamentos"
            icon={<CheckCircleIcon color="primary" />}
          />
        )}
      </List>
    </Section>
  );
}

function BeneficiosPlanoAssinar({ plano }: { plano: Plano }) {
  return (
    <div>
      <Typography variant="h6">
        Quer ter acesso a benefícios exclusivos?
      </Typography>
      <List>
        {plano?.permitirDependentes && plano.maximoDependentes > 0 && (
          <ListItem
            primary={`Até ${plano?.maximoDependentes} dependentes`}
            icon={<CheckCircleIcon color="primary" />}
          />
        )}
        <ListItem
          primary="Descontos de até 80% em consultas"
          icon={<CheckCircleIcon color="primary" />}
        />
        <ListItem
          primary="Descontos de até 80% em exames"
          icon={<CheckCircleIcon color="primary" />}
        />
        {plano?.telemedicina && (
          <ListItem
            primary="Telemedicina gratuita para qualquer dia e hora, sem agendamentos"
            icon={<CheckCircleIcon color="primary" />}
          />
        )}
      </List>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <ButtonLink variant="contained" color="primary" to="/app/assinar">
          Quero assinar
        </ButtonLink>
      </Box>
    </div>
  );
}

function SkeletonBeneficiosPlanoAssinar() {
  return (
    <Box>
      <Skeleton variant="text" sx={{ fontSize: "1.25rem" }} />

      <Box sx={{ py: 1 }}>
        <Skeleton variant="text" sx={{ my: 1 }} />
        <Skeleton variant="text" sx={{ my: 1 }} />
        <Skeleton variant="text" sx={{ my: 1 }} />
        <Skeleton variant="text" sx={{ my: 1 }} />
        <Skeleton
          variant="rounded"
          sx={{ mx: "auto", height: "2.25rem", width: "10rem" }}
        />
      </Box>
    </Box>
  );
}

export default function MinhaContaPage() {
  useTitle("Minha conta");

  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { loading, cliente, contratante, dependentes, plano, assinatura } =
    useAppSelector((state) => state.cliente);

  const { loading: loadingPlano, plano: planoAssinar } = useAppSelector(
    (state) => state.planos
  );

  async function load(abortController?: AbortController) {
    try {
      dispatch({ type: "SET_LOADING_CLIENTE", payload: true });

      await assinaturaApi
        .verifyAndUpdate()
        .then((verification) => {
          if (!verification.update) return;

          if (verification.redirectToPayment) {
            !!verification.newPlanoId &&
              localStorage.setItem(
                PROPOSTA_PLANO_ID_KEY,
                verification.newPlanoId.toString()
              );

            window.location.href = "/app/assinar";
          }
        })
        .catch(() => {
          enqueueSnackbar(
            "Um erro inesperado aconteceu ao realizar a verificação da sua assinatura. Se o erro persistir, entre em contato com o suporte.",
            "error"
          );
        });

      const cliente = await clienteApi.load(
        {
          assinatura: true,
          plano: true,
          dependentes: true,
          contratante: true,
        },
        abortController
      );

      if (!cliente?.plano || !cliente?.assinatura) {
        throw new Error(
          "Não foi possível obter as informações da sua assinatura."
        );
      }

      dispatch({ type: "SET_CLIENTE_PLANO", payload: cliente.plano });
      dispatch({
        type: "SET_CLIENTE_ASSINATURA",
        payload: cliente.assinatura,
      });

      if (cliente?.contratante) {
        dispatch({
          type: "SET_CLIENTE_CONTRATANTE",
          payload: cliente?.contratante,
        });
      }

      if (cliente?.dependentes) {
        dispatch({
          type: "SET_CLIENTE_DEPENDENTES",
          payload: cliente?.dependentes,
        });
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED") {
        return;
      }

      enqueueSnackbar(
        error?.message ||
          "Não foi possível obter as informações da sua assinatura.",
        "error"
      );
    } finally {
      dispatch({ type: "SET_LOADING_CLIENTE", payload: false });
    }
  }

  async function loadPlanoAssinar(abortController?: AbortController) {
    try {
      if (cliente.planoId !== 1) return;

      dispatch({ type: "SET_LOADING_PLANO", payload: true });

      const propostaPlanoId = localStorage.getItem(PROPOSTA_PLANO_ID_KEY);

      const planoId =
        propostaPlanoId && !isNaN(+propostaPlanoId)
          ? +propostaPlanoId
          : DEFAULT_PLANO_ID;

      const plano = await planoApi.findById(planoId, abortController);

      dispatch({ type: "SET_PLANO", payload: plano });
    } catch (error) {
      if (error?.code === "ERR_CANCELED") {
        return;
      }

      enqueueSnackbar(
        error?.message ||
          "Não foi possível carregar as informações do plano a contratar.",
        "error"
      );
    } finally {
      dispatch({ type: "SET_LOADING_PLANO", payload: false });
    }
  }

  useEffect(() => {
    const abortController = new AbortController();

    if (!loading) {
      load(abortController);
    }

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    if (cliente && cliente.planoId === 1) {
      loadPlanoAssinar(abortController);
    }

    return () => {
      abortController.abort();
    };
  }, [cliente]);

  return (
    <Box
      sx={{
        display: "flex",
        flexFlow: "column nowrap",
        alignItems: "center",
        gap: theme.spacing(2),
        height: "100%",
      }}
    >
      <Box sx={{ textAlign: "center", pb: 2 }}>
        <Typography variant="h4">Minha conta</Typography>
        <Typography>
          Aqui você pode ver as informações da sua conta e da sua assinatura.
        </Typography>
      </Box>

      {loading || !cliente || !plano || !assinatura ? (
        <SectionSkeleton />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexFlow: "column nowrap",
            gap: theme.spacing(2),
            [theme.breakpoints.up("lg")]: {
              flexFlow: "row nowrap",
              alignItems: "flex-start",
            },
          }}
        >
          <Section
            sx={{
              display: "flex",
              flexFlow: "column nowrap",
              gap: theme.spacing(2),
            }}
          >
            <div>
              <Typography variant="h6">Plano</Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {plano?.nome}
              </Typography>
            </div>

            {!!cliente &&
              !!assinatura &&
              cliente.tipoConta === "C" &&
              assinatura.getnetSubscriptionId && (
                <div>
                  <Typography variant="h6">Forma de pagamento</Typography>
                  <Typography>
                    Cartão de crédito
                    {(!!assinatura?.bandeiraCartao ||
                      !!assinatura?.finalCartao) &&
                      `:${
                        assinatura?.bandeiraCartao
                          ? ` ${assinatura.bandeiraCartao}`
                          : ""
                      }${
                        assinatura?.finalCartao
                          ? ` com final ${assinatura.finalCartao}`
                          : ""
                      }`}
                  </Typography>
                  <Typography>
                    Próxima cobrança:{" "}
                    {DateTime.fromISO(
                      assinatura.dataProximoPagamento
                    ).toLocaleString(DateTime.DATE_FULL, { locale: "pt-br" })}
                  </Typography>
                  <ButtonLink
                    to="/app/atualizar-pagamento"
                    variant="outlined"
                    color="primary"
                    sx={{ mt: 1.5 }}
                  >
                    Atualizar cartão
                  </ButtonLink>
                </div>
              )}

            {cliente.tipoConta === "C" ? (
              <div>
                <Typography variant="h6">Dependentes</Typography>
                {plano.permitirDependentes && plano.maximoDependentes > 0 ? (
                  <>
                    <Typography>
                      {dependentes?.length || "0"}/{plano.maximoDependentes}{" "}
                      utilizados
                    </Typography>

                    {!!dependentes && dependentes.length > 0 ? (
                      <List>
                        {dependentes.map((dependente, index) => (
                          <ListItem
                            avatar={
                              <Avatar color="primary">
                                {dependente.nome.trim()[0]}
                              </Avatar>
                            }
                            primary={formatName(dependente.nome.trim())}
                            key={index}
                          />
                        ))}
                      </List>
                    ) : !dependentes ? (
                      <Typography>
                        Erro ao carregar as informações dos seus dependentes.
                      </Typography>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <Typography>Seu plano não permite dependentes.</Typography>
                )}
              </div>
            ) : (
              <div>
                <Typography variant="h6">Titular do plano</Typography>
                {!!contratante ? (
                  <Stack
                    spacing={2}
                    direction="row"
                    alignItems="center"
                    sx={{ pt: 2 }}
                  >
                    <Avatar color="primary">
                      {contratante.nome.trim()[0]}
                    </Avatar>
                    <Typography>{formatName(contratante.nome)}</Typography>
                  </Stack>
                ) : (
                  <Typography>
                    Erro ao carregar as informações do contratante.
                  </Typography>
                )}
              </div>
            )}

            {plano.id === 1 &&
              (loadingPlano ? (
                <SkeletonBeneficiosPlanoAssinar />
              ) : (
                <BeneficiosPlanoAssinar plano={planoAssinar} />
              ))}
          </Section>

          {plano.id > 1 && <BeneficiosPlanoAtual plano={plano} />}
        </Box>
      )}
    </Box>
  );
}
